import { createContext, ReactNode, useEffect, useRef, RefObject } from 'react';
import { QUERY_GEOMETRY } from '@/graphql/queries/geometry';
import { QUERY_MUNICIPALITY_SCORE_MAP } from '@/graphql/queries/municipalities';
import { Geometry } from '@/graphql/types/geometry';
import { useApolloClient } from '@apollo/client';
import { useState } from 'react';
import { Classification, ClassificationOpportunityAndRisk } from '@/types/classification';
import { useLocation } from 'react-router-dom';
import { AppRouters } from '@/utils/constants/routers';
import type { DimensionLevelFilter } from '@/components/organisms/searchMunicipality/form/dimensions';
import { polygonDatabaseService } from '@/services/indexedDB/polygonDatabase';
import type {
  MunicipalityScoreMapResponse,
  MunicipalityScoreMapFilters,
} from '@/graphql/types/municipality';

export type GeometryData = {
  municipality_code: string;
  code_uf: string;
  name_municipality: string;
  name_uf: string;
  geometry?: Geometry;
  sigla_uf?: string;
  biome: string;
  biomes?: {
    id: string;
    municipality_code: string;
    biome: string;
  }[];
  center_point: number[];
  name_region: string;
  population: number | string;
  classification_type: Classification;
  dimensions_scores: {
    score_risk: string;
    score_opportunity: string;
    dimension: {
      id: number;
      name: string;
    };
    intensity_color_opportunity: string;
    intensity_color_risk: string;
    classification_risk: ClassificationOpportunityAndRisk;
    classification_opportunity: ClassificationOpportunityAndRisk;
  }[];
  municipality_score: {
    general_score_risk: number | string;
    general_score_opportunity: number | string;
    intensity_color_risk: string;
    intensity_color_opportunity: string;
    classification_risk: ClassificationOpportunityAndRisk;
    classification_opportunity: ClassificationOpportunityAndRisk;
  };
};

interface SearchFilter {
  dynamicFilters?: DynamicFilter[];
  state?: string;
  municipality?: {
    cdMun: string;
    name: string;
  };
}

export interface DynamicFilter {
  field: string;
  operator: string;
  value: string | number | string[] | number[];
}

export interface GeometryFilters {
  dynamicFilters?: DynamicFilter[];
  dimensionLevelFilters?: DimensionLevelFilter[];
  additionalMunicipalityCodes?: string[];
  latitude?: number;
  longitude?: number;
  maxPopulation?: number;
  minPopulation?: number;
  zoom?: number;
  municipality?: {
    cdMun: string;
    name: string;
  };
  order?: 'ASC' | 'DESC';
  orderBy?: string;
  orderType?: 'risk' | 'opportunity';
}

interface GeometyPagination {
  page?: number;
  limit?: number;
}

interface GetGeometryProps {
  filters?: GeometryFilters;
  pagination?: GeometyPagination;
  language?: 'en-US' | 'pt-BR';
}

interface GetGeometryForMapProps {
  filters: MunicipalityScoreMapFilters;
  language?: 'en-US' | 'pt-BR';
}

interface GeometryContextProps {
  geometry: GeometryData[];
  geometryTotal?: number;
  setGeometryTotal: (total: number) => void;
  loading: boolean;
  filter: GeometryFilters | undefined;
  handleLoading: (loading: boolean) => void;
  getGeometry: (props: GetGeometryProps) => Promise<void>;
  getGeometryForMap: (props: GetGeometryForMapProps) => Promise<void>;
  handleFilter: (filter: GeometryFilters | undefined) => void;
  handleGeometry: (geometry: GeometryData[]) => void;
  handleSearchFilter: (filter: SearchFilter | undefined) => void;
  searchFilter: GeometryFilters | undefined;
  savedMapData: GeometryData[];
  setSavedMapData: (data: GeometryData[]) => void;
  clearSavedMapData: () => void;
  currentZoom: number;
  setCurrentZoom: (zoom: number) => void;
  advancedSearchTotalResults: number;
  setAdvancedSearchTotalResults: (total: number) => void;
  hasFitBoundsRef: RefObject<boolean>;
  resetFitBounds: () => void;
  startSearch: boolean;
  setStartSearch: (start: boolean) => void;
  isMunicipality: boolean;
  setIsMunicipality: (isMunicipality: boolean) => void;
  selectedMunicipalityData: GeometryData | null;
  setSelectedMunicipalityData: (data: GeometryData | null) => void;
}

export const GeometryContext = createContext<GeometryContextProps>({} as GeometryContextProps);

export const GeometryProvider = ({ children }: { children: ReactNode }) => {
  const apolloClient = useApolloClient();
  const pathname = useLocation();

  const hasFitBoundsRef = useRef<boolean>(false);
  const geometryRequestGenerationRef = useRef(0);

  const [filter, setFilter] = useState<GeometryFilters>();
  const [searchFilter, setSearchFilter] = useState<GeometryFilters>();
  const [geometry, setGeometry] = useState<GeometryData[]>([]);
  const [geometryTotal, setGeometryTotal] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [savedMapData, setSavedMapData] = useState<GeometryData[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(0);
  const [advancedSearchTotalResults, setAdvancedSearchTotalResults] = useState<number>(0);
  const [startSearch, setStartSearch] = useState<boolean>(false);
  const [isMunicipality, setIsMunicipality] = useState<boolean>(false);
  const [selectedMunicipalityData, setSelectedMunicipalityData] = useState<GeometryData | null>(
    null
  );

  const resetFitBounds = () => {
    hasFitBoundsRef.current = false;
  };

  const handleFilter = (filter: GeometryFilters | undefined) => {
    setFilter(filter);
  };

  const handleSearchFilter = (filter: GeometryFilters | undefined) => {
    setSearchFilter(filter);
  };

  const handleGeometry = (geometry: GeometryData[]) => {
    setGeometry(geometry);
  };

  const handleLoading = (loading: boolean) => {
    setLoading(loading);
  };

  const clearSavedMapData = () => {
    setSavedMapData([]);
  };

  const getGeometry = async ({ filters, pagination, language }: GetGeometryProps) => {
    const generation = ++geometryRequestGenerationRef.current;
    setLoading(true);
    try {
      const { data } = await apolloClient.query<{
        advancedfilteredMunicipalities: {
          data: GeometryData[];
          total: number;
        };
      }>({
        query: QUERY_GEOMETRY,
        variables: {
          filters: { ...filters },
          pagination: { page: pagination?.page, limit: pagination?.limit },
          language: language,
        },
        fetchPolicy: 'no-cache',
      });

      if (generation !== geometryRequestGenerationRef.current) {
        return;
      }

      const municipalityCodes = data.advancedfilteredMunicipalities.data.map(
        (item) => item.municipality_code
      );

      const polygons = await Promise.all(
        municipalityCodes.map((code) => polygonDatabaseService.getPolygonByCode(code))
      );

      if (generation !== geometryRequestGenerationRef.current) {
        return;
      }

      const enrichedData = data.advancedfilteredMunicipalities.data.map((item, index) => {
        const polygon = polygons[index];
        return {
          ...item,
          geometry: polygon?.geometry,
        };
      });

      setGeometry(enrichedData);

      setGeometryTotal(data.advancedfilteredMunicipalities.total);
      handleFilter(filters);
      handleSearchFilter(filters);
    } finally {
      if (generation === geometryRequestGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const getGeometryForMap = async ({ filters, language }: GetGeometryForMapProps) => {
    const generation = ++geometryRequestGenerationRef.current;
    setLoading(true);
    try {
      const { data } = await apolloClient.query<{
        municipalityScoreMap: MunicipalityScoreMapResponse;
      }>({
        query: QUERY_MUNICIPALITY_SCORE_MAP,
        variables: {
          type: filters.type,
          channel: filters.channel,
          dynamicFilters: filters.dynamicFilters || [],
          dimensionLevelFilters: filters.dimensionLevelFilters || [],
          language: language,
        },
        fetchPolicy: 'no-cache',
      });

      if (generation !== geometryRequestGenerationRef.current) {
        return;
      }

      const municipalityCodes = Object.keys(data.municipalityScoreMap);

      const polygons = await Promise.all(
        municipalityCodes.map((code) => polygonDatabaseService.getPolygonByCode(code))
      );

      if (generation !== geometryRequestGenerationRef.current) {
        return;
      }

      const enrichedData: GeometryData[] = municipalityCodes.map((code, index) => {
        const scoreData = data.municipalityScoreMap[code];
        const polygon = polygons[index];

        const intensityColor = scoreData.intensity_color;

        return {
          municipality_code: code,
          code_uf: '',
          name_municipality: '',
          name_uf: '',
          geometry: polygon?.geometry,
          biome: '',
          center_point: [],
          name_region: '',
          population: 0,
          classification_type: scoreData.classification as Classification,
          dimensions_scores: [],
          municipality_score: {
            general_score_risk: filters.type === 'risk' ? String(scoreData.score) : '0',
            general_score_opportunity:
              filters.type === 'opportunity' ? String(scoreData.score) : '0',
            intensity_color_risk: filters.type === 'risk' ? intensityColor : '',
            intensity_color_opportunity: filters.type === 'opportunity' ? intensityColor : '',
            classification_risk:
              filters.type === 'risk'
                ? (scoreData.classification as ClassificationOpportunityAndRisk)
                : ClassificationOpportunityAndRisk.LOW,
            classification_opportunity:
              filters.type === 'opportunity'
                ? (scoreData.classification as ClassificationOpportunityAndRisk)
                : ClassificationOpportunityAndRisk.LOW,
          },
        };
      });

      setGeometry(enrichedData);
      setGeometryTotal(municipalityCodes.length);
    } finally {
      if (generation === geometryRequestGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (pathname.pathname === AppRouters.home) {
      handleFilter(undefined);
      handleSearchFilter(undefined);
    }
  }, [pathname.pathname]);

  return (
    <GeometryContext.Provider
      value={{
        geometry,
        geometryTotal,
        setGeometryTotal,
        loading,
        filter,
        getGeometry,
        getGeometryForMap,
        handleLoading,
        handleFilter,
        handleGeometry,
        handleSearchFilter,
        searchFilter,
        savedMapData,
        setSavedMapData,
        clearSavedMapData,
        currentZoom,
        setCurrentZoom,
        advancedSearchTotalResults,
        setAdvancedSearchTotalResults,
        hasFitBoundsRef,
        resetFitBounds,
        startSearch,
        setStartSearch,
        isMunicipality,
        setIsMunicipality,
        selectedMunicipalityData,
        setSelectedMunicipalityData,
      }}
    >
      {children}
    </GeometryContext.Provider>
  );
};
